import { useState, useEffect } from 'react';

import useCart from 'hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'hooks/use-ui';
import PublicEnv from 'shared/utils/public-env';

const FETCH_TIMEOUT_MS = 10000;

export type VectorProductsQueryParams = {
  scopeId: string;
  productIds: string[];
  limit: number;
  menuType?: string;
  menuVariant?: string;
  brandBoost?: string;
  boostFactor?: number;
};

export type VectorRecommendationsResponse = {
  success: boolean;
  data?: {
    // eslint-disable-next-line camelcase
    similar_product_ids: string[];
  };
  error?: string;
};

type UseVectorRecommendationsParams = {
  productIds: string[];
  limit: number;
  skip?: boolean;
};

type UseVectorRecommendationsReturn = {
  productIds: string[];
  loading: boolean;
  error: Error | undefined;
};

function buildVectorRecommendationsUrl(params: {
  dispensaryId: string;
  productIds: string[];
  limit: number;
  menuType?: string;
  menuVariant?: string;
}): string | null {
  if (!PublicEnv.vectorPersonalizeApiUrl) {
    return null;
  }

  const url = new URL(`${String(PublicEnv.vectorPersonalizeApiUrl)}/products/similar`);
  url.searchParams.append('scopeId', params.dispensaryId);

  params.productIds.forEach((id) => url.searchParams.append('productIds', id));
  url.searchParams.append('limit', params.limit.toString());

  if (params.menuType) {
    url.searchParams.append('menuType', params.menuType);
  }
  if (params.menuVariant) {
    url.searchParams.append('menuVariant', params.menuVariant);
  }

  return url.toString();
}

async function fetchVectorRecommendations(url: string, signal: AbortSignal): Promise<string[]> {
  const response = await fetch(url, {
    method: 'GET',
    signal,
  });

  if (!response.ok) {
    throw new Error(`Vector recommendations service returned ${response.status}`);
  }

  const result: VectorRecommendationsResponse = await response.json();

  if (result.success && result.data?.similar_product_ids) {
    return result.data.similar_product_ids;
  }

  if (result.error) {
    throw new Error(result.error);
  }

  return [];
}

export function useVectorRecommendations({
  productIds,
  limit,
  skip = false,
}: UseVectorRecommendationsParams): UseVectorRecommendationsReturn {
  const { dispensary } = useDispensary();
  const { menuType } = useCart();
  const { variant: menuVariant } = useUI();

  const [data, setData] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(!skip); // Start as loading if not skipped
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    if (skip) {
      setLoading(false);
      return undefined;
    }

    const validProductIds = productIds.filter((id): id is string => typeof id === 'string' && id.length > 0);
    const shouldSkip = !dispensary?.id || !menuType || !menuVariant || validProductIds.length === 0;

    if (shouldSkip) {
      setData([]);
      setLoading(false);
      setError(undefined);
      return undefined;
    }

    let isCancelled = false;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    const loadRecommendations = async (): Promise<void> => {
      setLoading(true);
      setError(undefined);

      try {
        const url = buildVectorRecommendationsUrl({
          dispensaryId: dispensary.id,
          productIds: validProductIds,
          limit,
          menuType,
          menuVariant,
        });

        if (!url) {
          setData([]);
          setLoading(false);
          return;
        }

        const recommendedProductIds = await fetchVectorRecommendations(url, controller.signal);

        if (!isCancelled) {
          setData(recommendedProductIds);
          setLoading(false);
        }
      } catch (err) {
        if (!isCancelled) {
          const errorMessage = err instanceof Error ? err.message : 'Unknown error fetching vector recommendations';
          setError(new Error(errorMessage));
          setData([]);
          setLoading(false);
        }
      } finally {
        clearTimeout(timeoutId);
      }
    };

    void loadRecommendations();

    return () => {
      isCancelled = true;
      controller.abort();
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, dispensary?.id, menuType, menuVariant, limit, JSON.stringify(productIds)]);

  return {
    productIds: data,
    loading,
    error,
  };
}
